<template>
  <div>
    <a-card :loading="loading">
      <a-page-header :title="planData.name" :sub-title="`测试计划详情 #${planData.key}`" @back="toPlanList">
        <template slot="extra">
          <a-button v-tc="{ name: '编辑' }" type="primary" @click="onEdit">编辑</a-button>
          <a-dropdown>
            <a-menu slot="overlay">
              <a-menu-item key="1" v-tc="{ name: '复制' }" @click="onCopy(planId)"> 复制</a-menu-item>
              <a-menu-item key="2" v-tc="{ name: '关联用例(更多)' }" @click="onAddCase()"> 关联用例</a-menu-item>
              <a-menu-item key="3" v-tc="{ name: '删除' }" @click="onDelete(planId)"> 删除</a-menu-item>
              <a-menu-item v-if="planData.is_locked === 0" key="4" v-tc="{ name: '归档' }" @click="onLock(planId)"> 归档</a-menu-item>
              <a-menu-item v-if="planData.is_locked === 1" key="4" v-tc="{ name: '已归档' }" :disabled="planData.is_locked"> 已归档</a-menu-item>
            </a-menu>
            <a-button>
              更多
              <a-icon type="down" />
            </a-button>
          </a-dropdown>
        </template>
        <a-descriptions size="small" :column="3">
          <a-descriptions-item label="当前状态">
            <test-plan-status-edit :plan-id="planData.id" :next-status="planData.next_status" :status="planData.status" @change="getTestPlanDetail" />
          </a-descriptions-item>
          <a-descriptions-item label="类型">
            {{ planData.type !== null && testPlanTypes[planData.type] ? testPlanTypes[planData.type].label : '--' }}
          </a-descriptions-item>
          <a-descriptions-item label="测试负责人">
            <user-profiles :value="planData.handlers.map(item => item.id)" avatar />
          </a-descriptions-item>
          <a-descriptions-item label="开始时间"> {{ planData.start_time || '--' }}</a-descriptions-item>
          <a-descriptions-item label="结束时间"> {{ planData.end_time || '--' }}</a-descriptions-item>
          <a-descriptions-item label="测试执行进度">
            <a-progress :percent="planData.progress" size="small" style="width: 180px" />
          </a-descriptions-item>
          <a-descriptions-item label="已完成/总数">
            <span>{{ planData.finished_cases }}/{{ planData.total_cases }}</span>
          </a-descriptions-item>
          <a-descriptions-item label="创建人"><user-profiles v-if="planData.creator" :value="[planData.creator.id]" avatar /></a-descriptions-item>
          <a-descriptions-item label="创建时间"> {{ planData.created_time }}</a-descriptions-item>
          <a-descriptions-item label="更新时间"> {{ planData.updated_time }}</a-descriptions-item>
        </a-descriptions>
      </a-page-header>
      <a-tabs v-model="planTabsActive" @change="handleTabChange">
        <a-tab-pane key="1" tab="详细信息">
          <div style="background: #fafafa; padding: 16px">
            <vditor-preview :content="planData.description" />
          </div>
        </a-tab-pane>
        <a-tab-pane key="2" tab="关联用例" force-render>
          <plan-case-list
            ref="caseList"
            :plan-id="planData.id"
            @add-case="onAddCase"
            @refresh-detail="getTestPlanDetail"
            @check-requirement-review="handleCheckRequirementReview"
          />
        </a-tab-pane>
        <a-tab-pane key="3" tab="关联缺陷" force-render>
          <story-issue-list :issue-list="issueList" :loading="issueListLoading" />
        </a-tab-pane>
        <a-tab-pane key="5" tab="关联需求" force-render>
          <plan-story-list :data="planData.stories" />
        </a-tab-pane>
        <a-tab-pane key="6" tab="关联任务" force-render>
          <task-list :data-source="planData.tasks" />
        </a-tab-pane>
        <a-tab-pane key="4" tab="变更历史" force-render>
          <test-plan-record ref="record" :plan-id="planData.id" />
        </a-tab-pane>
      </a-tabs>
      <comment-panel mode="testplan" :source-id="planData.id" />
    </a-card>
    <a-back-top />
    <test-plan-modal :is-show="modalVisible" :plan-id="planId" @close="close" />
    <case-selector :is-show="caseSelectorVisible" :plan-bind-keys="planBindCases" :ok-loading="okLoading" @close="closeSelector" />

    <!-- 需求提审检查结果弹窗 -->
    <a-modal
      :visible="requirementReviewModalVisible"
      :title="requirementReviewModalTitle"
      :footer="null"
      width="500px"
      @cancel="closeRequirementReviewModal"
    >
      <div v-if="requirementReviewResults.length > 0" style="text-align: center;">
        <div v-for="result in requirementReviewResults" :key="result.id" style="margin-bottom: 20px; padding: 16px; border: 1px solid #f0f0f0; border-radius: 6px; background-color: #fafafa;">
          <div style="font-weight: bold; margin-bottom: 12px; font-size: 16px; color: #262626;">需求 {{ result.id }}</div>
          <div v-if="result.can_review" style="color: #52c41a; font-size: 14px;">
            <a-icon type="check-circle" style="margin-right: 6px;" />
            可以提审
          </div>
          <div v-else style="color: #faad14; font-size: 14px;">
            <a-icon type="close-circle" style="margin-right: 6px;" />
            不能提审: {{ result.reason }}
          </div>
        </div>
      </div>
      <div v-else style="text-align: center; color: #999; padding: 40px 0; font-size: 14px;">
        <a-icon type="info-circle" style="font-size: 24px; margin-bottom: 12px; display: block;" />
        当前测试计划没有关联需求
      </div>

      <div style="text-align: center; margin-top: 32px; padding-top: 24px; border-top: 1px solid #f0f0f0;">
        <a-button style="margin-right: 12px; min-width: 80px;" @click="closeRequirementReviewModal">
          取消
        </a-button>
        <a-button
          v-if="hasReviewableRequirements"
          type="primary"
          style="min-width: 80px;"
          @click="goToRequirementPage"
        >
          去提审
        </a-button>
        <a-button
          v-else
          type="primary"
          style="min-width: 80px;"
          @click="goToRequirementPage"
        >
          跳转需求
        </a-button>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { moduleList } from '@/api/project/case';
import { issueList } from '@/api/project/issue';
import { testPlanTypes } from '@/utils/dictionary';
import { bindTestCases, planBindCaseIds, testPlanCopy, testPlanDelete, testPlanDetail, testPlanLock } from '@/api/project/testPlan';
import { checkReqEnable } from '@/api/data/workflow';

import CommentPanel from '@/components/comment/CommentPanel';
import VditorPreview from '@/components/VditorEditor/preview';
import TaskList from '@/pages/project/workflow/components/TaskList';
import PlanCaseList from '@/pages/project/testplan/components/PlanCaseList';
import CaseSelector from '@/pages/project/testplan/components/CaseSelector';
import StoryIssueList from '@/pages/project/story/components/StoryIssueList';
import PlanStoryList from '@/pages/project/testplan/components/PlanStoryList';
import TestPlanModal from '@/pages/project/testplan/components/TestPlanModal';
import TestPlanRecord from '@/pages/project/testplan/components/TestPlanRecord';
import TestPlanStatusEdit from '@/pages/project/testplan/components/TestPlanStatusEdit';

export default {
  name: 'Detail',
  components: {
    TaskList,
    CommentPanel,
    PlanCaseList,
    CaseSelector,
    TestPlanModal,
    VditorPreview,
    PlanStoryList,
    TestPlanRecord,
    StoryIssueList,
    TestPlanStatusEdit
  },
  data() {
    return {
      testPlanTypes,
      loading: false,
      planTabsActive: '1',
      planData: {
        handlers: []
      },
      editPlanId: null,
      modalVisible: false,
      projectId: parseInt(this.$route.params.project_id) || null,
      planId: parseInt(this.$route.query.planId) || null,
      activeKey: [],
      planBindCases: [],
      caseSelectorVisible: false,
      caseData: [],
      issueList: [],
      issueListLoading: false,
      okLoading: false,
      requirementReviewModalVisible: false,
      requirementReviewModalTitle: '需求提审检查结果',
      requirementReviewResults: [],
      tabItems: [
        { key: '1', label: '详细信息' },
        { key: '2', label: '关联用例' },
        { key: '3', label: '关联缺陷' },
        { key: '4', label: '变更历史' },
        { key: '5', label: '关联需求' },
        { key: '6', label: '关联任务' }
      ]
    };
  },
  computed: {
    handlers() {
      return record => {
        return record.handlers || [];
      };
    },
    hasReviewableRequirements() {
      return this.requirementReviewResults.some(result => result.can_review === true);
    }
  },
  created() {
    if (this.planId === null) {
      this.$router.push({ name: 'page:project:quality:testplan' });
    } else {
      this.getTestPlanDetail();
    }
    // this.getCaseList();
    // this.getIssueList();
  },
  methods: {
    handleTabChange(activeKey) {
      const item = this.tabItems.find(item => item.key === activeKey);
      this.utils.trackClick({ name: '切换卡片', label: item ? item.label : activeKey });
      if (activeKey === '2') {
        this.$refs.caseList.getCaseList();
      } else if (activeKey === '3') {
        this.getIssueList();
      } else if (activeKey === '4') {
        this.$refs.record.getTestPlanRecord();
      }
    },
    getCaseList() {
      moduleList({ project_id: this.projectId, detailed: true }).then(res => {
        this.moduleCases = res.data.module_cases;
        const items = res.data.items.map(item => {
          return {
            id: item.id,
            title: item.title,
            key: 'module-' + item.key,
            children: [],
            parent_id: item.parent_id
          };
        });
        const caseData = [
          {
            id: 0,
            key: 'module-0',
            title: '未分配',
            children: this.moduleCases['null'],
            count: this.moduleCases['null'].length,
            scopedSlots: { title: 'title' }
          },
          ...this.listToTree(items)
        ];
        // this.caseData = handleTreeData(caseData);
        this.caseData = caseData;
      });
    },
    listToTree(list, parentId = 0) {
      const menuObj = {};
      const tempArr = [];
      list.forEach(item => {
        const temp = {
          ...item,
          children: this.moduleCases[item.id.toString()] || [],
          count: this.moduleCases[item.id.toString()] ? this.moduleCases[item.id.toString()].length : 0,
          scopedSlots: { title: 'title' }
        };
        tempArr.push(temp);
        menuObj[item.id] = temp;
      });

      return tempArr.filter(item => {
        if (item.parent_id !== parentId) {
          menuObj[item.parent_id].children.push(item);
          return false;
        }
        return true;
      });
    },
    onDelete(id) {
      const self = this;
      this.$confirm({
        title: '确认',
        content: '确认要删除该测试计划吗',
        okText: '确定',
        okType: 'danger',
        cancelText: '取消',
        async onOk() {
          testPlanDelete({ project_id: self.projectId, test_plan_id: id }).then(() => {
            self.$message.success('删除成功');
            self.getPlanList();
          });
        }
      });
    },
    onCopy(planId) {
      testPlanCopy({ test_plan_id: planId }).then(() => {
        this.$message.success('复制计划成功');
        this.getPlanList();
      });
    },
    onLock(planId) {
      testPlanLock({ plan_id: planId, project_id: this.projectId }).then(() => {
        this.$message.success('归档成功');
        this.getPlanList();
      });
    },
    onAddCase() {
      planBindCaseIds({ test_plan_id: this.planId }).then(res => {
        this.planBindCases = res.data.items;
        this.caseSelectorVisible = true;
      });
    },
    closeSelector(targetKeys = null) {
      if (targetKeys !== null) {
        this.bindCases(targetKeys);
      } else {
        this.caseSelectorVisible = false;
      }
    },
    bindCases(testCases) {
      this.okLoading = true;
      bindTestCases({ test_plan_id: this.planId, cases: testCases })
        .then(() => {
          this.okLoading = false;
          this.caseSelectorVisible = false;
          this.$message.success('关联用例成功');
          this.$refs.caseList.getCaseList();
        })
        .catch(() => {
          this.okLoading = false;
          this.caseSelectorVisible = false;
        });
    },
    // 获取缺陷列表
    getIssueList() {
      this.issueListLoading = true;
      issueList({ plan_id: this.planId, project_id: this.projectId })
        .then(res => {
          this.issueList = res.data.items;
          this.issueListLoading = false;
        })
        .catch(() => {
          this.issueListLoading = false;
        });
    },
    toPlanList() {
      this.$router.push({ name: 'page:project:quality:testplan' });
    },
    // 点击编辑
    onEdit() {
      this.editPlanId = this.planData.id;
      this.modalVisible = true;
    },
    getTestPlanDetail() {
      this.loading = true;
      testPlanDetail({ test_plan_id: this.planId, project_id: this.projectId }).then(res => {
        this.planData = res.data;
        this.activeKey = [];
        this.activeKey.push('1');
        this.loading = false;
      });
    },
    close() {
      console.log('11111');
      this.modalVisible = false;
      this.getTestPlanDetail();
    },
    // 处理检查需求提审条件
    handleCheckRequirementReview() {
      console.log('当前project_id:', this.projectId);
      console.log('关联的需求:', this.planData.stories);

      if (this.planData.stories && this.planData.stories.length > 0) {
        this.requirementReviewResults = [];
        let completedCount = 0;

        this.planData.stories.forEach(story => {
          checkReqEnable({
            issue_id: story.id,
            project_id: this.projectId,
            issue_type: 'requirement'
          }).then(res => {
            console.log('检查需求提审条件结果:', res);
            // 收集结果
            if (res.data) {
              this.requirementReviewResults.push({
                id: story.id,
                can_review: res.data.can_review,
                reason: res.data.reason || ''
              });
            }
            completedCount++;

            // 所有请求完成后显示弹窗
            if (completedCount === this.planData.stories.length) {
              this.showRequirementReviewModal();
            }
          }).catch(err => {
            console.error('检查需求提审条件失败:', err);
            this.requirementReviewResults.push({
              id: story.id,
              can_review: false,
              reason: '检查失败'
            });
            completedCount++;

            if (completedCount === this.planData.stories.length) {
              this.showRequirementReviewModal();
            }
          });
        });
      } else {
        this.requirementReviewResults = [];
        this.showRequirementReviewModal();
      }
    },

    // 显示需求提审检查结果弹窗
    showRequirementReviewModal() {
      this.requirementReviewModalVisible = true;
    },

    // 关闭需求提审检查结果弹窗
    closeRequirementReviewModal() {
      this.requirementReviewModalVisible = false;
      this.requirementReviewResults = [];
    },

    // 跳转到需求提审页面
    goToRequirementReview() {
      // 这里可以跳转到需求提审页面
      this.$message.info('跳转到需求提审页面');
      this.closeRequirementReviewModal();
    },

    // 跳转到需求页面
    goToRequirementPage() {
      this.$router.push('/project/' + this.projectId + '/storyDetail?storyId=' + this.planData.stories[0].id);
      this.closeRequirementReviewModal();
    }
  }
};
</script>

<style lang="less" scoped>
/deep/ .ant-card-body {
  padding: 16px;
}

/deep/ .ant-page-header {
  padding: 0;
}
</style>
